import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="text-sm text-gray-700 leading-relaxed space-y-3">{children}</div>
  </div>
);

const TermsPage = () => (
  <>
    <Helmet><title>Terms of Service - MME</title></Helmet>

    <div className="min-h-screen bg-[#F9F7F4] py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="mme-link text-sm hover:underline mb-8 inline-block">&larr; Back to home</Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-1">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-10">Effective: April 10, 2026 &middot; Last updated: April 11, 2026</p>

        <Section title="1. Agreement to Terms">
          <p>By accessing or using the MME (Memory Management Engine) service operated by Railtech Inc.
          (&quot;Railtech,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), you agree to be bound by
          these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, do not use the
          service. These Terms, together with our{' '}
          <Link to="/privacy" className="mme-link hover:underline">Privacy Policy</Link>, constitute the
          entire agreement between you and Railtech regarding your use of MME.</p>
        </Section>

        <Section title="2. Eligibility">
          <p>You must be at least 18 years old (or the age of majority in your jurisdiction) to use MME. By
          creating an account, you represent that you meet this requirement and that the information you provide
          is accurate. Accounts must be created by humans; automated account creation is prohibited.</p>
        </Section>

        <Section title="3. Service Description">
          <p>MME is a memory management engine that provides persistent, tagged memory storage for AI
          applications. The service includes:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>A REST API for saving, retrieving, and searching memories</li>
            <li>Automatic tag extraction powered by AI (Anthropic Claude)</li>
            <li>Relevance-scored memory retrieval (inject)</li>
            <li>An MCP (Model Context Protocol) server for Claude Desktop integration</li>
            <li>A web dashboard for managing your account and viewing your data</li>
          </ul>
        </Section>

        <Section title="4. Account Responsibilities">
          <p>You are responsible for:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Maintaining the security of your API keys and authentication credentials</li>
            <li>All activity that occurs under your account, whether authorized by you or not</li>
            <li>Notifying us immediately at{' '}
              <a href="mailto:info@railtech.io" className="mme-link hover:underline">info@railtech.io</a>{' '}
              if you suspect unauthorized access to your account</li>
          </ul>
          <p>We are not liable for any loss or damage arising from your failure to secure your credentials.</p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree not to use MME to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Store, transmit, or distribute content that is illegal, harmful, threatening, abusive,
            defamatory, or otherwise objectionable</li>
            <li>Attempt to access, read, modify, or delete another user&apos;s data</li>
            <li>Circumvent or attempt to circumvent rate limits, authentication, or security measures</li>
            <li>Use the service for competitive analysis, reverse engineering, or to build a competing product</li>
            <li>Introduce malware, viruses, or any code designed to disrupt the service</li>
            <li>Scrape, crawl, or use automated tools to extract data from the service beyond normal API usage</li>
            <li>Use the service in any way that violates applicable local, provincial, national, or
            international law</li>
          </ul>
        </Section>

        <Section title="6. API Keys and Rate Limits">
          <p>API keys are personal and non-transferable. Each key is bound to a single user account. You may
          not share, sell, or distribute your API key. If you believe your key has been compromised, rotate it
          immediately using the dashboard.</p>
          <p>We impose rate limits on API endpoints to ensure fair usage and protect service stability.
          Exceeding rate limits will result in HTTP 429 responses. Sustained abuse of rate limits may result in
          account suspension.</p>
        </Section>

        <Section title="7. Intellectual Property">
          <p><strong>Your content:</strong> You retain full ownership of all memory content you store in MME.
          We claim no intellectual property rights over your data. By using the service, you grant us a limited,
          non-exclusive license to process your content solely for the purpose of providing the MME service
          (storing, tagging, retrieving, and displaying your memories).</p>
          <p><strong>Our service:</strong> The MME software, APIs, documentation, branding, and website are
          the intellectual property of Railtech Inc. You may not copy, modify, distribute, reverse-engineer,
          or create derivative works of our service without prior written consent.</p>
        </Section>

        <Section title="8. Service Availability">
          <p>MME is provided on an &quot;as-is&quot; and &quot;as-available&quot; basis. We make reasonable
          efforts to maintain high availability but do not guarantee uninterrupted or error-free access. We may
          perform maintenance, updates, or upgrades that temporarily affect availability.</p>
          <p>There is no Service Level Agreement (SLA) for the free tier. We will make reasonable efforts to
          communicate planned downtime in advance.</p>
        </Section>

        <Section title="9. API Changes">
          <p>We may modify, extend, or deprecate API endpoints. For breaking changes, we will provide
          reasonable advance notice (minimum 30 days) via the email address associated with your account.
          Non-breaking additions (new fields, new endpoints) may be made without notice.</p>
        </Section>

        <Section title="10. Termination">
          <p><strong>By you:</strong> You may stop using MME at any time. You may request deletion of your
          account and all associated data by emailing{' '}
          <a href="mailto:info@railtech.io" className="mme-link hover:underline">info@railtech.io</a>.</p>
          <p><strong>By us:</strong> We may suspend or terminate your account immediately and without notice
          if you violate these Terms, engage in abusive behavior, or if we are required to do so by law. We
          may also discontinue the service entirely with 90 days advance notice.</p>
          <p>Upon termination, we will delete your data within 30 days unless retention is required by law.</p>
        </Section>

        <Section title="11. Disclaimer of Warranties">
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, MME IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES OF
          ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO WARRANTIES
          OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT
          THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT ANY DEFECTS WILL BE CORRECTED.</p>
        </Section>

        <Section title="12. Limitation of Liability">
          <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL RAILTECH INC., ITS
          DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
          OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING
          OUT OF OR IN CONNECTION WITH:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Your use of or inability to use the service</li>
            <li>Any unauthorized access to or alteration of your data</li>
            <li>Any third-party conduct on the service</li>
            <li>Any content stored through the service</li>
          </ul>
          <p>Our total aggregate liability for all claims related to the service shall not exceed the amount
          you paid us in the 12 months preceding the claim, or CAD $100, whichever is greater.</p>
        </Section>

        <Section title="13. Indemnification">
          <p>You agree to indemnify and hold harmless Railtech Inc. and its officers, directors, employees,
          and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising
          from your use of the service, your violation of these Terms, or your violation of any third-party
          rights.</p>
        </Section>

        <Section title="14. Force Majeure">
          <p>We shall not be liable for any failure or delay in performing our obligations where such failure
          or delay results from circumstances beyond our reasonable control, including but not limited to:
          natural disasters, pandemics, war, terrorism, labor disputes, government actions, power failures,
          internet or telecommunications failures, or failures of third-party service providers (including
          cloud hosting and AI providers).</p>
        </Section>

        <Section title="15. Governing Law and Dispute Resolution">
          <p>These Terms are governed by and construed in accordance with the laws of the Province of Ontario,
          Canada, without regard to its conflict of law provisions. Any disputes arising under or in connection
          with these Terms shall be subject to the exclusive jurisdiction of the courts located in Ontario,
          Canada.</p>
        </Section>

        <Section title="16. Changes to These Terms">
          <p>We reserve the right to modify these Terms at any time. We will notify you of material changes
          by posting the updated Terms on this page and, for significant changes, by emailing the address
          associated with your account at least 30 days before the changes take effect. Your continued use of
          MME after the effective date constitutes acceptance of the revised Terms. If you do not agree to the
          revised Terms, you must stop using the service and may request account deletion.</p>
        </Section>

        <Section title="17. Severability">
          <p>If any provision of these Terms is found to be unenforceable or invalid by a court of competent
          jurisdiction, the remaining provisions shall continue in full force and effect.</p>
        </Section>

        <Section title="18. Contact Us">
          <p>If you have questions about these Terms, please contact us:</p>
          <div className="bg-white border border-gray-200 rounded-lg p-4 mt-2">
            <p className="font-medium text-gray-900">Railtech Inc.</p>
            <p>Email:{' '}<a href="mailto:info@railtech.io" className="mme-link hover:underline">info@railtech.io</a></p>
            <p>Ontario, Canada</p>
          </div>
        </Section>
      </div>
    </div>
  </>
);

export default TermsPage;
